import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getProjects, getBusinessPlans, getBusinessPlan, createBusinessPlan, updateBusinessPlan, deleteBusinessPlan } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Plus, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const BusinessPlan = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project') || '';
  
  const [projects, setProjects] = useState([]);
  const [businessPlans, setBusinessPlans] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_year: new Date().getFullYear(),
    years: 5,
    revenue_growth_rate: 5,
    cogs_percentage: 60,
    opex_percentage: 20,
    capex_year1: 0,
    depreciation_years: 5,
    tax_rate: 24,
    working_capital_days: 60,
    initial_revenue: 0
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchBusinessPlans();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (id) {
      fetchPlanDetail();
    }
  }, [id]);

  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (!selectedProject && response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessPlans = async () => {
    try {
      const response = await getBusinessPlans(selectedProject);
      setBusinessPlans(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPlanDetail = async () => {
    try {
      const response = await getBusinessPlan(id);
      setCurrentPlan(response.data);
      const bp = response.data;
      setFormData({
        name: bp.name,
        description: bp.description || '',
        start_year: bp.start_year,
        years: bp.years,
        ...bp.assumptions
      });
      setSelectedProject(bp.project_id);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleSave = async () => {
    if (!selectedProject) {
      toast.error('Seleziona un progetto');
      return;
    }
    if (!formData.name) {
      toast.error('Inserisci un nome per il business plan');
      return;
    }

    setSaving(true);
    try {
      const data = {
        project_id: selectedProject,
        name: formData.name,
        description: formData.description,
        start_year: parseInt(formData.start_year),
        years: parseInt(formData.years),
        revenue_growth_rate: parseFloat(formData.revenue_growth_rate),
        cogs_percentage: parseFloat(formData.cogs_percentage),
        opex_percentage: parseFloat(formData.opex_percentage),
        capex_year1: parseFloat(formData.capex_year1),
        depreciation_years: parseInt(formData.depreciation_years),
        tax_rate: parseFloat(formData.tax_rate),
        working_capital_days: parseInt(formData.working_capital_days),
        initial_revenue: parseFloat(formData.initial_revenue)
      };

      let response;
      if (currentPlan) {
        response = await updateBusinessPlan(currentPlan.id, data);
      } else {
        response = await createBusinessPlan(data);
      }
      
      setCurrentPlan(response.data);
      fetchBusinessPlans();
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentPlan || !window.confirm('Sei sicuro di voler eliminare questo business plan?')) return;
    
    try {
      await deleteBusinessPlan(currentPlan.id);
      setCurrentPlan(null);
      setFormData({
        name: '',
        description: '',
        start_year: new Date().getFullYear(),
        years: 5,
        revenue_growth_rate: 5,
        cogs_percentage: 60,
        opex_percentage: 20,
        capex_year1: 0,
        depreciation_years: 5,
        tax_rate: 24,
        working_capital_days: 60,
        initial_revenue: 0
      });
      fetchBusinessPlans();
      toast.success(t('common.success'));
      navigate(`/business-plan?project=${selectedProject}`);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleNewPlan = () => {
    setCurrentPlan(null);
    setFormData({
      name: '',
      description: '',
      start_year: new Date().getFullYear(),
      years: 5,
      revenue_growth_rate: 5,
      cogs_percentage: 60,
      opex_percentage: 20,
      capex_year1: 0,
      depreciation_years: 5,
      tax_rate: 24,
      working_capital_days: 60,
      initial_revenue: 0
    });
    navigate(`/business-plan?project=${selectedProject}`);
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="business-plan-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('bp.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {currentPlan ? currentPlan.name : 'Nuovo business plan'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleNewPlan} data-testid="new-plan-btn">
            <Plus size={16} className="mr-2" />
            Nuovo
          </Button>
          {currentPlan && (
            <Button variant="outline" onClick={handleDelete} className="text-destructive" data-testid="delete-plan-btn">
              <Trash2 size={16} className="mr-2" />
              {t('common.delete')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="save-btn">
            <Save size={16} className="mr-2" />
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </div>

      {/* Project Selection */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Progetto</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Seleziona progetto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} - {p.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Business Plan Salvati</Label>
              <Select 
                value={currentPlan?.id || ''} 
                onValueChange={(value) => {
                  navigate(`/business-plan/${value}?project=${selectedProject}`);
                }}
              >
                <SelectTrigger data-testid="saved-plans-select">
                  <SelectValue placeholder="Seleziona piano" />
                </SelectTrigger>
                <SelectContent>
                  {businessPlans.map(bp => (
                    <SelectItem key={bp.id} value={bp.id}>{bp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="assumptions" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl w-full sm:w-auto">
          <TabsTrigger value="assumptions" className="rounded-lg" data-testid="tab-assumptions">{t('bp.assumptions')}</TabsTrigger>
          <TabsTrigger value="projections" className="rounded-lg" data-testid="tab-projections">{t('bp.projections')}</TabsTrigger>
          <TabsTrigger value="charts" className="rounded-lg" data-testid="tab-charts">Grafici</TabsTrigger>
        </TabsList>

        {/* Assumptions Tab */}
        <TabsContent value="assumptions">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('bp.assumptions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('bp.name')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Business Plan 2024-2028"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Descrizione opzionale"
                    data-testid="input-description"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>{t('bp.startYear')}</Label>
                  <Input
                    type="number"
                    value={formData.start_year}
                    onChange={(e) => updateField('start_year', e.target.value)}
                    data-testid="input-start-year"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bp.years')}</Label>
                  <Select value={String(formData.years)} onValueChange={(v) => updateField('years', v)}>
                    <SelectTrigger data-testid="input-years">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3, 4, 5, 6, 7, 8, 9, 10].map(y => (
                        <SelectItem key={y} value={String(y)}>{y} anni</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('bp.initialRevenue')}</Label>
                  <Input
                    type="number"
                    value={formData.initial_revenue}
                    onChange={(e) => updateField('initial_revenue', e.target.value)}
                    data-testid="input-initial-revenue"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bp.revenueGrowth')}</Label>
                  <Input
                    type="number"
                    value={formData.revenue_growth_rate}
                    onChange={(e) => updateField('revenue_growth_rate', e.target.value)}
                    step="0.1"
                    data-testid="input-growth"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>{t('bp.cogsPercent')}</Label>
                  <Input
                    type="number"
                    value={formData.cogs_percentage}
                    onChange={(e) => updateField('cogs_percentage', e.target.value)}
                    step="0.1"
                    data-testid="input-cogs"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bp.opexPercent')}</Label>
                  <Input
                    type="number"
                    value={formData.opex_percentage}
                    onChange={(e) => updateField('opex_percentage', e.target.value)}
                    step="0.1"
                    data-testid="input-opex"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bp.taxRate')}</Label>
                  <Input
                    type="number"
                    value={formData.tax_rate}
                    onChange={(e) => updateField('tax_rate', e.target.value)}
                    step="0.1"
                    data-testid="input-tax"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bp.wcDays')}</Label>
                  <Input
                    type="number"
                    value={formData.working_capital_days}
                    onChange={(e) => updateField('working_capital_days', e.target.value)}
                    data-testid="input-wc-days"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('bp.capex')}</Label>
                  <Input
                    type="number"
                    value={formData.capex_year1}
                    onChange={(e) => updateField('capex_year1', e.target.value)}
                    data-testid="input-capex"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bp.depreciationYears')}</Label>
                  <Input
                    type="number"
                    value={formData.depreciation_years}
                    onChange={(e) => updateField('depreciation_years', e.target.value)}
                    min={1}
                    max={20}
                    data-testid="input-depreciation"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projections Tab */}
        <TabsContent value="projections">
          {currentPlan?.projections ? (
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardHeader>
                <CardTitle>{t('bp.projections')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="font-semibold">{t('common.year')}</TableHead>
                        <TableHead className="text-right font-semibold">Ricavi</TableHead>
                        <TableHead className="text-right font-semibold">COGS</TableHead>
                        <TableHead className="text-right font-semibold">Gross Profit</TableHead>
                        <TableHead className="text-right font-semibold">OPEX</TableHead>
                        <TableHead className="text-right font-semibold">EBITDA</TableHead>
                        <TableHead className="text-right font-semibold">EBIT</TableHead>
                        <TableHead className="text-right font-semibold">Utile Netto</TableHead>
                        <TableHead className="text-right font-semibold">FCF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentPlan.projections.map((p, index) => (
                        <TableRow key={index} data-testid={`projection-row-${index}`}>
                          <TableCell className="font-medium">{p.year}</TableCell>
                          <TableCell className="text-right number-format">{p.revenue.toLocaleString()}</TableCell>
                          <TableCell className="text-right number-format text-red-600">-{p.cogs.toLocaleString()}</TableCell>
                          <TableCell className="text-right number-format">{p.gross_profit.toLocaleString()}</TableCell>
                          <TableCell className="text-right number-format text-red-600">-{p.opex.toLocaleString()}</TableCell>
                          <TableCell className="text-right number-format font-medium">{p.ebitda.toLocaleString()}</TableCell>
                          <TableCell className="text-right number-format">{p.ebit.toLocaleString()}</TableCell>
                          <TableCell className="text-right number-format font-medium text-green-600">{p.net_income.toLocaleString()}</TableCell>
                          <TableCell className="text-right number-format font-medium">{p.fcf.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12 text-center text-muted-foreground">
                <TrendingUp size={48} className="mx-auto mb-4 opacity-30" />
                <p>Salva il business plan per visualizzare le proiezioni</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Charts Tab */}
        <TabsContent value="charts">
          {currentPlan?.projections ? (
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Ricavi e Utile Netto</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={currentPlan.projections}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="year" stroke="#64748b" />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          formatter={(value) => value.toLocaleString()}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="revenue" 
                          name="Ricavi"
                          stroke="#6366f1" 
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="net_income" 
                          name="Utile Netto"
                          stroke="#22c55e" 
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>EBITDA e FCF</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={currentPlan.projections}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="year" stroke="#64748b" />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          formatter={(value) => value.toLocaleString()}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Legend />
                        <Bar dataKey="ebitda" name="EBITDA" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="fcf" name="FCF" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>Salva il business plan per visualizzare i grafici</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BusinessPlan;
